/**
 * AlertDetailsTable — CRUD-style table for the Alert Details Report.
 *
 * Follows AreasPage.jsx patterns: COLUMN_DEFS, sort, filter, kebab menu,
 * and active filter bar. Column visibility toggle is managed by the parent.
 *
 * Props:
 *   data               — paginated rows for current page
 *   allData            — full UNFILTERED dataset (source for filter option values;
 *                        must not have column filters applied, so options stay stable)
 *   visibleColumns     — string[] of visible column keys
 *   sortColumn         — current sort column key
 *   sortOrder          — 'asc' | 'desc'
 *   onSort             — (columnKey) => void
 *   filters            — { columnKey: [value1, ...] }
 *   onFilterChange     — (columnKey, selectedValues) => void
 *   onClearFilters     — () => void
 *   onAddNote          — (event) => void
 *   onEditReason       — (event) => void
 *   eventNotesActive   — boolean
 *   isReasonEditable   — boolean
 *   tzOffset           — timezone offset for datetime display
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal, convertIsoToLocalDate } from '../../../../libs.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';
import NotesCell from '../../../../components/v2/NotesCell.jsx';
import CallsCell from '../../../../components/v2/CallsCell.jsx';

// ---------------------------------------------------------------------------
// Helpers for date-based filter transforms
// ---------------------------------------------------------------------------

/** Extract just the hour label (e.g. "3 PM", "12 AM") from a local datetime string like "MM/dd/yyyy h:mmAM" */
function extractHourLabel(isoDatetime) {
    const local = convertIsoToLocal(isoDatetime);
    if (!local) return '';
    // local format: "MM/dd/yyyy h:mmAM" — extract hour + AM/PM
    const match = local.match(/(\d{1,2}):\d{2}(AM|PM)/i);
    if (!match) return '';
    return `${match[1]} ${match[2].toUpperCase()}`;
}

/** Extract just the date (e.g. "04/10/2026") from a local datetime string */
function extractDateLabel(isoDatetime) {
    return convertIsoToLocalDate(isoDatetime) || '';
}

/** Sort hour labels chronologically (12 AM, 1 AM, ... 11 AM, 12 PM, 1 PM, ... 11 PM) */
function sortHourLabels(a, b) {
    const toIdx = (label) => {
        const m = label.match(/^(\d{1,2})\s*(AM|PM)$/i);
        if (!m) return 99;
        let h = parseInt(m[1], 10);
        const isPM = m[2].toUpperCase() === 'PM';
        if (h === 12) h = 0;
        return isPM ? h + 12 : h;
    };
    return toIdx(a) - toIdx(b);
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export const COLUMN_DEFS = [
    { key: 'notes', label: 'Notes', sortable: false, filterable: false },
    // Calls column is only surfaced for Grandstream campuses (gated by isGrandstream).
    { key: 'calls', label: 'Calls', sortable: false, filterable: false },
    { key: 'event_datetime_local', label: 'Event Start', sortable: true, filterable: true, required: true },
    { key: 'event_type_name', label: 'Category/Type', sortable: true, filterable: true },
    { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
    { key: 'area_name', label: 'Area', sortable: true, filterable: true },
    { key: 'description', label: 'Device / Serial', sortable: true, filterable: false },
    { key: 'display_name', label: 'Accepted By', sortable: true, filterable: true },
    { key: 'ack_datetime_local', label: 'Cleared', sortable: true, filterable: false },
    { key: 'finish_datetime_local', label: 'Finished', sortable: true, filterable: false },
    { key: 'response_time', label: 'Response (min)', sortable: true, filterable: true },
    { key: 'service_time', label: 'Service Time (min)', sortable: true, filterable: true },
    { key: 'reason_code_description', label: 'Reason', sortable: true, filterable: true }
];

// Build column labels map for ActiveFilterBar
const COLUMN_LABELS = {};
COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// RowActions (kebab menu)
// ---------------------------------------------------------------------------

const RowActions = ({ row, onEditReason, isReasonEditable }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const handleToggle = (e) => {
        e.stopPropagation();
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button
                type="button"
                className="ap-row-actions__trigger"
                onClick={handleToggle}
                aria-label="Actions"
                aria-expanded={open}
            >
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <Link
                        to={`/profiles/event/${row.event_id}`}
                        className="ap-row-actions__item"
                        style={{ textDecoration: 'none' }}
                        onClick={() => setOpen(false)}
                    >
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        View Details
                    </Link>
                    {isReasonEditable ? (
                        <button
                            type="button"
                            className="ap-row-actions__item"
                            onClick={(e) => { e.stopPropagation(); setOpen(false); onEditReason(row); }}
                        >
                            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                            Edit Reason
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Reason cell — auto-close sentinels (-1, -2) get a dotted underline + popover
// ---------------------------------------------------------------------------
const AUTO_CLOSE_REASON_MESSAGES = {
    '-1': 'This event was automatically closed by the system due to autoclear timeout.',
    '-2': 'This event was automatically closed by the system due to auto finish timeout.',
};

const ReasonCell = ({ reasonId, description, edited }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const message = AUTO_CLOSE_REASON_MESSAGES[String(reasonId)];
    const text = description || '\u2014';

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const editedBadge = edited ? (
        <span className="ar-reason-badge" title="This value has been edited">
            <i className="fe fe-edit-2" style={{ fontSize: '10px' }} />
        </span>
    ) : null;

    if (!message) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                <span className="dp-cell-text">{text}</span>
                {editedBadge}
            </div>
        );
    }

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverW = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const centerX = rect.left + rect.width / 2;
        let left = centerX - popoverW / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
        setStyle({
            position: 'fixed',
            zIndex: 9999,
            left: `${left}px`,
            top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            width: `${popoverW}px`,
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ position: 'relative', display: 'inline-block' }}>
                <span className="dp-cell-text has-popover">{text}</span>
                {open ? (
                    <span className="na-type-popover" style={style}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
                    </span>
                ) : null}
            </span>
            {editedBadge}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function renderCell(row, colKey) {
    switch (colKey) {
        case 'event_datetime_local':
            return (
                <Link to={`/profiles/event/${row.event_id}`} className="dp-link">
                    {row.event_datetime_local ? convertIsoToLocal(row.event_datetime_local) : '\u2014'}
                </Link>
            );

        case 'event_type_name':
            return (
                <div>
                    <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {row.event_category_name || ''}
                    </span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.event_type_name || ''}
                    </span>
                </div>
            );

        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="dp-link">
                    {row.resident_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.resident_name || '\u2014'}</span>
            );

        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="dp-link">
                    {row.area_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.area_name || '\u2014'}</span>
            );

        case 'description': {
            // Stacked: description + manufacture_uuid. Conditional link by manufacture_id.
            const linkMap = { 1: '/profiles/inovonics', 4: '/profiles/palcare' };
            const linkPath = linkMap[row.manufacture_id];
            return (
                <div>
                    {linkPath && row.device_id ? (
                        <Link to={`${linkPath}/${row.device_id}`} className="dp-link">
                            {row.description || '\u2014'}
                        </Link>
                    ) : (
                        <span className="dp-cell-text">{row.description || '\u2014'}</span>
                    )}
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.manufacture_uuid || ''}
                    </span>
                </div>
            );
        }

        case 'display_name':
            return (
                <div>
                    <span className="dp-cell-text">{row.display_name || '\u2014'}</span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.accept_datetime_local ? convertIsoToLocal(row.accept_datetime_local) : ''}
                    </span>
                </div>
            );

        case 'ack_datetime_local':
            return (
                <span className="dp-cell-text">
                    {row.ack_datetime_local ? convertIsoToLocal(row.ack_datetime_local) : '\u2014'}
                </span>
            );

        case 'finish_datetime_local':
            return (
                <span className="dp-cell-text">
                    {row.finish_datetime_local ? convertIsoToLocal(row.finish_datetime_local) : '\u2014'}
                </span>
            );

        case 'response_time':
            return (
                <span className="dp-cell-text">
                    {row.response_time != null ? row.response_time : '\u2014'}
                </span>
            );

        case 'service_time': {
            const val = row.service_time;
            return (
                <span className="dp-cell-text">
                    {val == null || val === 0 || val === '0' || val === '0.0' ? '-' : val}
                </span>
            );
        }

        case 'reason_code_description':
            return (
                <ReasonCell
                    reasonId={row.reason_id}
                    description={row.reason_code_description}
                    edited={row.reason_edits && row.reason_edits.length > 0}
                />
            );

        default:
            return <span className="dp-cell-text">{row[colKey] != null ? String(row[colKey]) : '\u2014'}</span>;
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AlertDetailsTable = ({
    data,
    allData,
    visibleColumns,
    sortColumn,
    sortOrder,
    onSort,
    filters,
    onFilterChange,
    onClearFilters,
    onAddNote,
    onEditReason,
    eventNotesActive,
    isReasonEditable,
    tzOffset,
    dateFilterMode,  // 'hour' | 'date' | undefined (default = raw datetime strings)
    isGrandstream = false,
}) => {
    // Always show kebab — View Details is always available
    const showKebab = true;

    // Build unique values for filterable columns from allData
    const filterValues = useMemo(() => {
        const vals = {};
        COLUMN_DEFS.filter((c) => c.filterable).forEach((col) => {
            if (col.key === 'event_datetime_local' && dateFilterMode) {
                // Transform to hour or date labels (fields are already local — no offset needed)
                const extractor = dateFilterMode === 'hour' ? extractHourLabel : extractDateLabel;
                const labels = new Set((allData || []).map((row) => extractor(row.event_datetime_local)));
                labels.delete('');
                const sorted = [...labels];
                if (dateFilterMode === 'hour') {
                    sorted.sort(sortHourLabels);
                } else {
                    sorted.sort();
                }
                vals[col.key] = sorted;
            } else {
                const unique = [...new Set((allData || []).map((row) => {
                    const v = row[col.key];
                    return v != null ? String(v) : '';
                }))].sort();
                vals[col.key] = unique;
            }
        });
        return vals;
    }, [allData, dateFilterMode]);

    // Column labels for ActiveFilterBar
    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        const updated = current.filter((v) => v !== value);
        onFilterChange(columnKey, updated);
    }, [filters, onFilterChange]);

    // The Calls column only exists for Grandstream campuses. Gating here (in
    // addition to the page's column config) keeps it hidden even if a stale
    // 'calls' key lingers in visibleColumns after switching to a non-Grandstream campus.
    const visibleColDefs = COLUMN_DEFS.filter(
        (col) => visibleColumns.includes(col.key) && (col.key !== 'calls' || isGrandstream)
    );

    return (
        <div className="ap-table-container">
            {/* Active filter bar */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={onClearFilters}
            />

            {/* Table */}
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            {showKebab ? (
                                <th scope="col" className="ap-th ap-th--actions">
                                    <span className="ap-sr-only">Actions</span>
                                </th>
                            ) : null}
                            {visibleColDefs.map((col) => (
                                <th key={col.key} scope="col" className="ap-th">
                                    <div className="ap-th__content">
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => onSort(col.key)}
                                                className="ap-th__sort-btn"
                                                aria-label={`Sort by ${col.label}`}
                                            >
                                                {col.label}
                                                {sortColumn === col.key ? (
                                                    <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                        {sortOrder === 'asc'
                                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                        }
                                                    </svg>
                                                ) : null}
                                            </button>
                                        ) : (
                                            <span>{col.label}</span>
                                        )}

                                        {col.filterable ? (
                                            <FilterPopover
                                                columnKey={col.key}
                                                label={col.label}
                                                values={filterValues[col.key] || []}
                                                selectedValues={filters[col.key] || []}
                                                onChange={onFilterChange}
                                            />
                                        ) : null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td" colSpan={visibleColDefs.length + (showKebab ? 1 : 0)} style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No events found.</span>
                                </td>
                            </tr>
                        ) : null}
                        {data.map((row, idx) => (
                            <tr key={row.event_id || idx} className="dp-tr">
                                {showKebab ? (
                                    <td className="dp-td" style={{ textAlign: 'center' }}>
                                        <RowActions
                                            row={row}
                                            onEditReason={onEditReason}
                                            isReasonEditable={isReasonEditable}
                                        />
                                    </td>
                                ) : null}
                                {visibleColDefs.map((col) => (
                                    <td key={col.key} className="dp-td">
                                        {col.key === 'notes' ? (
                                            <NotesCell
                                                row={row}
                                                notes={row.notes}
                                                onClick={onAddNote}
                                                eventNotesActive={eventNotesActive}
                                                tzOffset={tzOffset}
                                                ariaPrefix={`Event ${row.event_id || ''}`}
                                            />
                                        ) : col.key === 'calls' ? (
                                            <CallsCell
                                                calls={row.calls}
                                                tzOffset={tzOffset}
                                                ariaPrefix={`Event ${row.event_id || ''}`}
                                            />
                                        ) : renderCell(row, col.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AlertDetailsTable;
